import React, { useEffect, useState } from 'react';
import { Container, Card, Button, Row, Col, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import { usePageLoader } from '../context/PageLoaderContext';
import ProductCard from '../components/product/ProductCard';
import './MyOrders.css';

// Helper function to get full image URL
const getImageUrl = (imagePath) => {
  if (!imagePath) return '/placeholder.png';

  // If it's already a full URL, return as is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }

  // If it starts with /uploads or similar, it's likely a server path
  // Try to construct the full URL
  if (imagePath.startsWith('/')) {
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    // Remove /api from the end if present, as images are served from root
    const baseUrl = API_BASE_URL.replace(/\/api$/, '');
    return `${baseUrl}${imagePath}`;
  }

  // Otherwise, return as is (might be a relative path from public folder)
  return imagePath;
};

const MyOrders = () => {
  const { isAuthenticated } = useAuth();
  const { setPageLoading } = usePageLoader();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrders, setExpandedOrders] = useState(new Set());
  const [bestsellers, setBestsellers] = useState([]);
  const [loadingBestsellers, setLoadingBestsellers] = useState(false);

  useEffect(() => {
    setPageLoading(loading);
    return () => setPageLoading(false);
  }, [loading, setPageLoading]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchOrders();
    }
    fetchBestsellers();
  }, [isAuthenticated]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await api.get('/user/orders');
      setOrders(response.orders || []);
      // Expand first order by default
      if (response.orders && response.orders.length > 0) {
        setExpandedOrders(new Set([response.orders[0].id]));
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBestsellers = async () => {
    try {
      setLoadingBestsellers(true);
      const response = await api.get('/products/bestsellers');
      setBestsellers(response.products || []);
    } catch (error) {
      console.error('Error fetching bestsellers:', error);
    } finally {
      setLoadingBestsellers(false);
    }
  };

  const toggleOrder = (orderId) => {
    const newExpanded = new Set(expandedOrders);
    if (newExpanded.has(orderId)) {
      newExpanded.delete(orderId);
    } else {
      newExpanded.add(orderId);
    }
    setExpandedOrders(newExpanded);
  };

  const getOrderStatusBadge = (status) => {
    if (!status) return null;
    const label = status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    const variantMap = {
      pending: 'secondary',
      confirmed: 'info',
      processing: 'primary',
      shipped: 'info',
      delivered: 'success',
      cancelled: 'danger',
      order_cancelled: 'danger',
      returned: 'warning',
      refund_initialized: 'warning',
      refunded: 'secondary',
    };
    return { label, variant: variantMap[status] || 'secondary' };
  };

  const formatDeliveryDate = (order) => {
    // Try to get delivery date from tracking, otherwise estimate
    if (order.tracking && order.tracking.length > 0) {
      const deliveredTracking = order.tracking.find((t) => t.status === 'delivered');
      if (deliveredTracking) {
        return (
          'Delivered On ' +
          new Date(deliveredTracking.created_at).toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
          })
        );
      }
    }

    // Estimate delivery date (7 days from order date)
    const orderDate = new Date(order.created_at);
    const deliveryDate = new Date(orderDate);
    deliveryDate.setDate(deliveryDate.getDate() + 7);

    return (
      'Delivery Expected On ' +
      deliveryDate.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    );
  };

  const handleBuyAgain = async (item) => {
    try {
      if (item.item_type === 'combo' && item.combo_id) {
        const comboSelections = (item.combo_selections_detail || []).map((d) => ({
          product_id: d.product_id,
          variant_id: d.variant_id,
          label: d.label || d.product_name,
        }));
        await api.post('/cart', {
          combo_id: item.combo_id,
          combo_selections: comboSelections,
          quantity: item.quantity,
        });
      } else {
        await api.post('/cart', {
          product_id: item.product_id,
          variant_id: item.variant_id,
          quantity: item.quantity,
        });
      }
      alert('Items added to cart!');
      window.dispatchEvent(new Event('cartUpdated'));
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert(error.response?.data?.error || 'Failed to add items to cart');
    }
  };

  if (loading) return null;

  return (
    <div className="orders-page">
      <Container>
        <h1 className="orders-title">Orders</h1>

        {orders.length === 0 ? (
          <div className="empty-orders-section">
            <Card className="empty-orders-card">
              <Card.Body className="text-center py-5">
                <p className="empty-orders-message">You have no orders yet.</p>
                <Link to="/products">
                  <Button variant="primary" className="start-shopping-btn">
                    Start Shopping
                  </Button>
                </Link>
              </Card.Body>
            </Card>

            {/* Bestsellers Section */}
            {bestsellers.length > 0 && (
              <div className="bestsellers-section mt-5">
                <h2 className="bestsellers-title">Bestsellers</h2>
                {loadingBestsellers ? (
                  <div className="text-center py-4">
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : (
                  <Row>
                    {bestsellers.map((product) => (
                      <Col key={product.id} md={4} sm={6} xs={6} lg={3} className="mb-4">
                        <ProductCard product={product} buttonText="Add to Cart" />
                      </Col>
                    ))}
                  </Row>
                )}
                <div className="text-center mt-4">
                  <Link to="/products?filter=bestsellers">
                    <Button variant="outline-dark" className="show-more-btn">
                      Show More
                    </Button>
                  </Link>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="orders-list">
            {orders.map((order) => {
              const isExpanded = expandedOrders.has(order.id);
              const deliveryDate = formatDeliveryDate(order);

              return (
                <Card key={order.id} className="order-card">
                  <Card.Body>
                    <div className="order-header">
                      <div className="order-info">
                        <div class="flex gap-2 items-start justify-start">
                          <h3 className="order-number">Order #{order.order_number}</h3>
                          {getOrderStatusBadge(order.order_status) && (
                            <Badge
                              bg={getOrderStatusBadge(order.order_status).variant}
                              className="order-status-badge mt-1"
                            >
                              {getOrderStatusBadge(order.order_status).label}
                            </Badge>
                          )}
                        
                        </div>
                          <p className="delivery-date">{deliveryDate}</p>
                      </div>
                      <div className="order-actions">
                        <Link to={`/orders/${order.id}`} className="order-action-link">
                          Track Order
                        </Link>
                        {order.order_status === 'delivered' ? (
                          <>
                            <span className="order-action-separator">|</span>
                            <Link to={`/returns/request/${order.id}`} className="order-action-link">
                              Return Items
                            </Link>
                          </>
                        ) : order.order_status === 'refunded' ||
                          order.order_status === 'refund_initialized' ? (
                          <>
                            <span className="order-action-separator">|</span>
                            <span
                              className="order-action-link disabled"
                              title="Refunded. You can't file return again."
                            >
                              Return Items
                            </span>
                          </>
                        ) : (
                          <>
                            <span className="order-action-separator">|</span>
                            <span
                              className="order-action-link disabled"
                              title="Return Items available only for delivered orders"
                            >
                              Return Items
                            </span>
                          </>
                        )}
                        <button
                          className="order-expand-btn"
                          onClick={() => toggleOrder(order.id)}
                          aria-label={isExpanded ? 'Collapse order' : 'Expand order'}
                        >
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className={isExpanded ? 'expanded' : ''}
                          >
                            <path d="M6 9l6 6 6-6" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {isExpanded && order.items && order.items.length > 0 && (
                      <div className="order-items">
                        {order.items.map((item) => {
                          const isCombo = item.item_type === 'combo';
                          const comboDetails = item.combo_selections_detail || [];
                          const variantString = !isCombo
                            ? [item.size, item.color_name].filter(Boolean).join(', ')
                            : '';
                          const itemLink = isCombo ? `/combos/${item.combo_id}` : `/products/${item.product_id}`;

                          return (
                          <div key={item.id} className="order-item">
                            <div className="order-item-image">
                              <img
                                src={getImageUrl(item.product_image)}
                                alt={item.product_name}
                                onError={(e) => {
                                  e.target.src = '/placeholder.png';
                                }}
                              />
                              {isCombo && <span className="order-item-unavailable-badge" style={{ background: '#16a34a' }}>COMBO</span>}
                            </div>
                            <div className="order-item-details">
                              <h4 className="order-item-name">{item.product_name}</h4>
                              {isCombo && (
                                <p className="order-item-quantity text-muted small mb-1">
                                  {item.combo_includes_label ||
                                    comboDetails.map((d) => d.product_name).filter(Boolean).join(' + ')}
                                </p>
                              )}
                              {isCombo && comboDetails.length > 0 && (
                                <div className="mb-2" style={{ display: 'grid', gap: 6 }}>
                                  {comboDetails.map((d, idx) => (
                                    <div key={d.variant_id || idx} style={{ display: 'flex', gap: 8, alignItems: 'center', border: '1px solid #eee', borderRadius: 8, padding: '6px 8px' }}>
                                      {d.product_image && (
                                        <img src={getImageUrl(d.product_image)} alt="" style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4 }} />
                                      )}
                                      <div>
                                        <div style={{ fontSize: 12, fontWeight: 600 }}>{d.product_name}</div>
                                        <div style={{ fontSize: 11, color: '#696C70' }}>{[d.color_name, d.size].filter(Boolean).join(' • ')}</div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {!isCombo && variantString && (
                                <p className="order-item-quantity text-muted small mb-1">{variantString}</p>
                              )}
                              <p className="order-item-quantity">Quantity: {item.quantity}</p>
                              <p className="order-item-price">
                                ₹
                                {(
                                  (parseFloat(item.final_amount) ||
                                    parseFloat(item.total_price) ||
                                    0) / item.quantity
                                ).toFixed(2)}
                              </p>
                            </div>
                            <div className="order-item-actions">
                              <Button
                                variant="dark"
                                className="buy-again-btn w-100"
                                onClick={() => handleBuyAgain(item)}
                              >
                                Buy Again
                              </Button>
                              <Link to={itemLink}>
                                <Button variant="outline-dark" className="view-product-btn">
                                  {isCombo ? 'View Combo' : 'View Product'}
                                </Button>
                              </Link>
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              );
            })}
          </div>
        )}
      </Container>
    </div>
  );
};

export default MyOrders;
